import React from 'react';

import { useAppDispatch, useAppSelector } from '../../../../app/hooks';
import { PunchCardsCard, DriverPunchCardsCard } from '../../components';
import {
  getPunchCardSummaries,
  refreshPunchCardSummaries,
  getCurrentPayPeriod,
  getPayPeriodById,
  getPayPeriodSummaries
} from '../../punchCardSlice';
import { PunchCardsForAllDrivers } from '../../../../models';

import './PunchCardsView.scss';

export function PunchCardsView() {
  const dispatch = useAppDispatch();

  const updatePunchCardAPI = useAppSelector(state => state.punchCards.updatePunchCardAPI);
  const createPunchCardAPI = useAppSelector(state => state.punchCards.createPunchCardAPI);
  const deletePunchCardAPI = useAppSelector(state => state.punchCards.deletePunchCardAPI);
  const punchCardSummaries = useAppSelector(state => state.punchCards.punchCardSummaries);
  const currentPayPeriod = useAppSelector(state => state.punchCards.currentPayPeriod);
  const payPeriodSummaries = useAppSelector(state => state.punchCards.payPeriodSummaries);

  const [detailsDriver, setDetailsDriver] = React.useState<PunchCardsForAllDrivers | undefined>();

  // Fetch current pay period on mount
  React.useEffect(() => {
    dispatch(getCurrentPayPeriod());
  }, []);

  // Fetch punch card summaries and pay period summaries when pay period changes
  React.useEffect(() => {
    if (currentPayPeriod) {
      dispatch(getPunchCardSummaries({
        fromDate: currentPayPeriod.payPeriod.startDate,
        toDate: currentPayPeriod.payPeriod.endDate,
      }));
      dispatch(getPayPeriodSummaries(currentPayPeriod.payPeriod.id));
    }
  }, [currentPayPeriod]);

  // Refetch when punch card is updated
  React.useEffect(() => {
    if (!updatePunchCardAPI.loading && !!updatePunchCardAPI.value && currentPayPeriod) {
      dispatch(refreshPunchCardSummaries({
        fromDate: currentPayPeriod.payPeriod.startDate,
        toDate: currentPayPeriod.payPeriod.endDate,
      }));
      dispatch(getPayPeriodSummaries(currentPayPeriod.payPeriod.id));
    }
  }, [updatePunchCardAPI.loading, !!updatePunchCardAPI.value]);

  // Refetch when punch card is created
  React.useEffect(() => {
    if (!createPunchCardAPI.loading && !!createPunchCardAPI.value && currentPayPeriod) {
      dispatch(refreshPunchCardSummaries({
        fromDate: currentPayPeriod.payPeriod.startDate,
        toDate: currentPayPeriod.payPeriod.endDate,
      }));
      dispatch(getPayPeriodSummaries(currentPayPeriod.payPeriod.id));
    }
  }, [createPunchCardAPI.loading, !!createPunchCardAPI.value]);

  // Refetch when punch card is deleted
  React.useEffect(() => {
    if (!deletePunchCardAPI.loading && !!deletePunchCardAPI.value && currentPayPeriod) {
      dispatch(refreshPunchCardSummaries({
        fromDate: currentPayPeriod.payPeriod.startDate,
        toDate: currentPayPeriod.payPeriod.endDate,
      }));
      dispatch(getPayPeriodSummaries(currentPayPeriod.payPeriod.id));
    }
  }, [deletePunchCardAPI.loading, !!deletePunchCardAPI.value]);

  // Update selected driver when punch card summaries change
  React.useEffect(() => {
    if (detailsDriver) {
      const currentDriverEmail = detailsDriver.driver.email;
      const summary = punchCardSummaries.find(s => s.driver.email === currentDriverEmail);

      if (summary) {
        setDetailsDriver(summary);
      }
    }
  }, [punchCardSummaries]);

  const onPreviousPayPeriod = () => {
    if (currentPayPeriod?.payPeriod.previousId) {
      dispatch(getPayPeriodById(currentPayPeriod.payPeriod.previousId));
    }
  };

  const onNextPayPeriod = () => {
    if (currentPayPeriod?.payPeriod.nextId) {
      dispatch(getPayPeriodById(currentPayPeriod.payPeriod.nextId));
    }
  };

  const onDriverDetailsClose = () => {
    setDetailsDriver(undefined);
  };

  // Get driver's pay period summary
  const getDriverPayPeriodSummary = (driverId: string) => {
    return payPeriodSummaries?.find(s => s.driverId === driverId);
  };

  return (
    <div className='punch-cards-view-component'>
      <PunchCardsCard
        currentPayPeriod={currentPayPeriod}
        onPreviousPayPeriod={onPreviousPayPeriod}
        onNextPayPeriod={onNextPayPeriod}
        viewDriverDetails={setDetailsDriver}
      />

      {!!detailsDriver && (
        <DriverPunchCardsCard
          summary={detailsDriver}
          driverPayPeriodSummary={getDriverPayPeriodSummary(detailsDriver.driver.id)}
          onClose={onDriverDetailsClose}
        />
      )}
    </div>
  );
}
